// Unless explicitly stated otherwise all files in this repository are licensed under the MIT License.
// This product includes software developed at Datadog (https://www.datadoghq.com/).
// Copyright 2019-Present Datadog, Inc.

import type { GetInternalPlugins, GetPluginsArg, PluginOptions, PluginName } from '@dd/core/types';

export const PLUGIN_NAME: PluginName = 'datadog-true-end-plugin' as const;

export const getTrueEndPlugins: GetInternalPlugins = (arg: GetPluginsArg) => {
    const { context } = arg;
    // TODO: Reconsider the lifecycle names around trueEnd and flush together.
    // Having a final hook after "trueEnd" is confusing, so these likely need a broader rename.
    const asyncHookFn = async () => {
        await context.asyncHook('asyncTrueEnd');
    };
    const flushHookFn = async () => {
        await context.asyncHook('flush');
    };
    const syncHookFn = () => {
        context.hook('syncTrueEnd');
    };
    const bothHookFns = async () => {
        syncHookFn();
        await asyncHookFn();
        await flushHookFn();
    };

    const xpackPlugin: PluginOptions['rspack'] & PluginOptions['webpack'] = (compiler) => {
        // NOTE: rspack prior to 1.2.* will randomly crash on shutdown.tapPromise.
        compiler.hooks.shutdown.tapPromise(PLUGIN_NAME, bothHookFns);
    };

    const rollupPlugin: PluginOptions['rollup'] & PluginOptions['vite'] = {
        async writeBundle() {
            // TODO: Need to fallback here in case the closeBundle isn't called.
        },
        async closeBundle() {
            await bothHookFns();
        },
    };

    return [
        {
            name: PLUGIN_NAME,
            enforce: 'post',
            webpack: xpackPlugin,
            esbuild: {
                setup(build) {
                    // NOTE: "onEnd" is the best we can do for esbuild, but it's very far from being the "true end" of the build.
                    build.onEnd(async () => {
                        await asyncHookFn();
                        await flushHookFn();
                    });
                    // NOTE: "onDispose" is strictly synchronous.
                    build.onDispose(() => {
                        syncHookFn();
                    });
                },
            },
            vite: rollupPlugin,
            rollup: rollupPlugin,
            rspack: xpackPlugin,
        },
    ];
};
